import { memo } from 'react'
import { PauseCircle, RefreshCw, WifiOff } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import { STALE_AFTER_MS } from '../config'
import { useNow } from '../hooks/use-environment'
import type { ConnectionStatus } from '../types'
import { formatSecondsAgo } from '../utils/time'

type Tone = 'green' | 'amber' | 'red' | 'grey'

const TONE: Record<Tone, { pill: string; dot: string }> = {
  green: { pill: 'border-emerald-200 bg-emerald-50 text-emerald-800', dot: 'bg-emerald-500' },
  amber: { pill: 'border-amber-200 bg-amber-50 text-amber-800', dot: 'bg-amber-500' },
  red: { pill: 'border-red-200 bg-red-50 text-red-700', dot: 'bg-red-500' },
  grey: { pill: 'border-slate-200 bg-slate-50 text-slate-600', dot: 'bg-slate-400' },
}

export const LiveStatusPill = memo(function LiveStatusPill({
  connection,
  isPaused,
  lastUpdatedAt,
  onRetry,
  timeZone,
}: {
  connection: ConnectionStatus
  isPaused: boolean
  lastUpdatedAt: number | null
  onRetry: () => void
  /** IANA zone for the "Last update" time; omitted keeps the browser zone. */
  timeZone?: string
}) {
  // Only this pill ticks every second; the rest of the page re-renders on data changes.
  const now = useNow(1_000, !isPaused)
  const ago = formatSecondsAgo(lastUpdatedAt, now)
  const isStale = lastUpdatedAt != null && now - lastUpdatedAt > STALE_AFTER_MS

  let tone: Tone
  let label: string
  let showAgo = true
  if (isPaused) {
    tone = 'grey'
    label = 'Paused'
  } else if (connection === 'offline') {
    tone = 'red'
    label = 'Offline'
  } else if (connection === 'reconnecting') {
    tone = 'amber'
    label = 'Reconnecting…'
  } else if (connection === 'connecting') {
    tone = 'grey'
    label = 'Connecting…'
    showAgo = false
  } else {
    tone = isStale ? 'amber' : 'green'
    label = 'Live'
  }

  const styles = TONE[tone]
  const pulsing = tone === 'green' && !isPaused

  return (
    <div className="flex items-center gap-2">
      <div
        className={cn('inline-flex h-8 items-center gap-2 rounded-full border px-3 text-xs font-semibold', styles.pill)}
        title={lastUpdatedAt ? `Last update ${new Date(lastUpdatedAt).toLocaleTimeString(undefined, timeZone ? { timeZone } : undefined)}${isStale ? ' — no updates for over 30s' : ''}` : undefined}
      >
        {isPaused ? (
          <PauseCircle className="h-3.5 w-3.5" aria-hidden="true" />
        ) : connection === 'offline' ? (
          <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
            {pulsing ? <span className={cn('absolute inline-flex h-full w-full rounded-full opacity-60 motion-safe:animate-ping', styles.dot)} /> : null}
            <span className={cn('relative inline-flex h-2.5 w-2.5 rounded-full', styles.dot)} />
          </span>
        )}
        {/* Announce state changes only, not the ticking counter. */}
        <span role="status" aria-live="polite">
          {label}
          {isPaused ? <span className="sr-only"> — updates paused while this tab is hidden</span> : null}
        </span>
        {showAgo && lastUpdatedAt != null ? (
          <span aria-hidden="true" className="font-medium opacity-80">· updated {ago}</span>
        ) : null}
      </div>
      {connection === 'offline' && !isPaused ? (
        <Button type="button" size="sm" variant="outline" className="h-8 gap-1.5 border-red-200 text-red-700 hover:bg-red-50" onClick={onRetry}>
          <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
          Retry
        </Button>
      ) : null}
    </div>
  )
})
